/**
 * Shared "carry the last known fish-stock count forward" primitive.
 *
 * Extracted from the `lotGrowthPerformance` Reports dataset (see the
 * comment on its own per-week bucket resolution in `reportDatasets.ts`)
 * so the same logic can also back a standalone derived-stock column on
 * other datasets (e.g. `feed`) without re-implementing or drifting from
 * the SGR/FCR calculation. A fish-stock count is recorded far less often
 * than a weight sample or a feed entry, so most rows/buckets in a lot's
 * history have no stock of their own — this fills those gaps with the
 * most recent prior non-zero count in the same lot's chronological
 * history.
 *
 * Pure: callers must pass items already sorted chronologically ascending
 * per lot (mixing lots in one call would carry one lot's count into
 * another's history).
 *
 * `getOwnStock` returns `null` for "no stock signal this item" (carry the
 * prior value forward) or a number — including `0` — for "trust this as
 * the real count." Callers must be able to tell a confirmed zero (a lot
 * fully depleted) apart from an item that simply has no stock event of
 * its own; collapsing both into `0` is what let a depleted lot's derived
 * stock carry its last positive count forward forever (v2.2 findings.md
 * #31).
 *
 * The return value carries the same distinction: `null` for every item
 * before the lot's first known stock signal (still genuinely unknown), a
 * number — including `0` — from the first signal onward. Callers that want
 * a plain number for arithmetic (e.g. `biomassKg = stock * weight`) should
 * coalesce with `?? 0` themselves; collapsing here would reintroduce the
 * same ambiguity this fix closes.
 */
export const carryForwardLastKnownStock = <T>(
  chronologicallyAscending: readonly T[],
  getOwnStock: (item: T) => number | null
): (number | null)[] => {
  let lastKnown: number | null = null
  return chronologicallyAscending.map((item) => {
    const own = getOwnStock(item)
    if (own !== null) lastKnown = own
    return lastKnown
  })
}
